import axios from "axios";
import { refreshLoggedInUser } from "./authApis";
import { ResponseValidator } from "../validatior/responseValidator";
import { handleSessionExpired } from "../../utils/sessionExpired";
import { toast } from "react-toastify";

const isRefreshTokenRequest = (config) =>
  config?.url?.includes("/auth/refresh-tokens");

/** Prefer localStorage so refreshed tokens from another tab are picked up. */
const getAccessToken = () =>
  localStorage.getItem("_t") ?? sessionStorage.getItem("_t");

const getRefreshToken = () =>
  localStorage.getItem("_rt") ?? sessionStorage.getItem("_rt");

let tokenRefreshPromise = null;

const getApiBaseUrl = () => {
  const baseUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
  return baseUrl.endsWith("/customer") ? baseUrl : `${baseUrl}/customer`;
};

/***
 * *axios request configs
 */
axios.interceptors.request.use((config) => {
  // startLoader()
  config.baseURL = getApiBaseUrl();
  const token = getAccessToken();
  if (token) {
    config.headers = {
      Authorization: `Bearer ${token}`,
    };
  } else {
    axios.defaults.headers.common.Authorization &&
      delete axios.defaults.headers.common.Authorization;
    config.headers = axios.defaults.headers.common;
  }
  return config;
});

/***
 * *axios response configs
 */
axios.interceptors.response.use(
  async (reuslt) => {
    const data = reuslt.data;
    if (data?.serverResponse) {
      return data;
    }
    const response = await ResponseValidator({
      isError: data?.isError,
      message: data?.message,
    });
    if (response) {
      return data;
    }
    return data;
  },
  async (error) => {

    const expectedErrorRefreshToken =
      error.response && error.response.status === 401;

    if (expectedErrorRefreshToken) {
      if (isRefreshTokenRequest(error.config)) {
        handleSessionExpired();
        return Promise.reject(error);
      }

      const checkLogin = async () => {
        const refreshToken = getRefreshToken();

        if (!refreshToken) {
          handleSessionExpired();
          return false;
        }

        const res = await refreshLoggedInUser({ refreshToken });
        if (!res?.access?.token) {
          handleSessionExpired();
          return false;
        }

        error.config.headers["Authorization"] = "Bearer " + res.access.token;
        if (error.config?.data) {
          error.config.data = JSON.parse(error.config.data);
        }
        error.config.baseURL = undefined;
        return await axios.request(error.config);
      };

      if (!tokenRefreshPromise) {
        tokenRefreshPromise = checkLogin().finally(() => {
          tokenRefreshPromise = null;
        });
      }
      const response = await tokenRefreshPromise;
      if (response) {
        return Promise.resolve(response);
      }
    }

    const expectedError =
      error.response &&
      error.response.status >= 400 &&
      error.response.status <= 500;
    if (!expectedError) {
      toast.error(
        "Unable to connect to the server. Please check your connection and try again."
      );
    } else {
      const message =
        error.response.data?.message ||
        error.response.data?.serverResponse?.message;
      if (message) {
        toast.error(message);
      }
    }
    return Promise.reject(error);
  }
);

export default {
  get: axios.get,
  post: axios.post,
  put: axios.put,
  delete: axios.delete,
  patch: axios.patch,
  interceptors: axios.interceptors,
};
